import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../config/supabase';
import type { Branch } from '../../config/supabase';

// Query Keys
export const branchesKeys = {
  all: ['branches'] as const,
  lists: () => [...branchesKeys.all, 'list'] as const,
  list: (page?: number, pageSize?: number, search?: string) =>
    [...branchesKeys.lists(), { page, pageSize, search }] as const,
};

// Fetch all branches (for dropdowns)
export const useBranchesAll = () => {
  return useQuery({
    queryKey: branchesKeys.list(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('branches')
        .select('*')
        .order('name');

      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

// Fetch branches with pagination
export const useBranches = (page: number = 1, pageSize: number = 10, search: string = '') => {
  return useQuery({
    queryKey: branchesKeys.list(page, pageSize, search),
    queryFn: async () => {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      let query = supabase
        .from('branches')
        .select('*', { count: 'exact' });

      if (search.trim()) {
        query = query.or(`name.ilike.%${search}%,description.ilike.%${search}%`);
      }

      query = query
        .order('name')
        .range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        branches: data || [],
        totalCount: count || 0,
      };
    },
    staleTime: 2 * 60 * 1000,
  });
};

// Create branch mutation
export const useCreateBranch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (branchData: Omit<Branch, 'id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('branches')
        .insert(branchData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: branchesKeys.all });
    },
  });
};

// Update branch mutation
export const useUpdateBranch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Branch> & { id: string }) => {
      const { data, error } = await supabase
        .from('branches')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: branchesKeys.all });
    },
  });
};

// Delete branch mutation
export const useDeleteBranch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('branches')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: branchesKeys.all });
    },
  });
};
